/**
 * Persiste o vínculo entre o pedido de origem e o pedido criado no destino.
 *
 * Deve ser chamado APÓS o create-orcamento no tenant destino, usando o token
 * de operação (para o vínculo ser gravado no banco do destino). O contexto de
 * operação é lido automaticamente pelo `api()` via override.
 */

import { api, type ApiOverrideOptions } from "../../../../lib/api";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { LogCategory } from "../../../../config/logging";
import { TokenService } from "../../../../lib/tokenService";
import { QuotationOperationContextStore } from "./quotationOperationContext";

/** Um contribuinte de origem no vínculo N->1 (SPEC-15 RN-04). */
export interface CrossTenantLinkSource {
	sourceOrcId: number;
	sourceOitId: number;
	sourceRole?: string | null;
	sourceClientId?: number | null;
}

export interface PersistCrossTenantLinkParams {
	targetClientId: number;
	targetOrcId: number;
	targetOitId: number;
	sourceOrcId?: number | null;
	sourceOitId?: number | null;
	sourceClientId?: number | null;
	/** Lista completa dos contribuintes de origem (N->1). Opcional. */
	sources?: CrossTenantLinkSource[];
}

/**
 * Envia o vínculo origem->destino. Só faz sentido quando há contexto de
 * operação roteado ativo; sem ele, a função é um no-op seguro.
 */
export async function persistCrossTenantLink(
	params: PersistCrossTenantLinkParams,
): Promise<{ linked: boolean; id?: number }> {
	const context = QuotationOperationContextStore.get();
	if (!context) {
		return { linked: false };
	}

	const override: ApiOverrideOptions = {
		accessTokenOverride: context.operationAccessToken,
		tenantIdOverride: context.effectiveTenantId,
	};

	sendToTerminal(
		"🔗 [CROSS_TENANT] Persistindo vínculo origem->destino",
		{
			targetTenantId: context.effectiveTenantId,
			targetOrcId: params.targetOrcId,
			targetOitId: params.targetOitId,
		},
		LogCategory.BUSINESS,
	);

	const response = (await api(
		"/cross-tenant/order-link",
		{
			method: "POST",
			body: JSON.stringify({
				targetClientId: params.targetClientId,
				targetOrcId: params.targetOrcId,
				targetOitId: params.targetOitId,
				sourceTenantId: context.sourceTenantId,
				sourceClientId: params.sourceClientId ?? null,
				sourceOrcId: params.sourceOrcId ?? null,
				sourceOitId: params.sourceOitId ?? null,
				// N->1: contribuintes de origem (RN-04). O backend usa esta lista;
				// as colunas source_* acima ficam como contribuinte primario.
				sources: params.sources?.map((s) => ({
					sourceOrcId: s.sourceOrcId,
					sourceOitId: s.sourceOitId,
					sourceRole: s.sourceRole ?? null,
					sourceClientId: s.sourceClientId ?? null,
				})),
			}),
		},
		override,
	)) as { success?: boolean; data?: { id?: number } };

	return { linked: Boolean(response?.success), id: response?.data?.id };
}

/** Um contribuinte de origem lido do vínculo persistido (SPEC-15 reload). */
export interface CrossTenantLinkSourceRead {
	sourceTenantId: string;
	sourceClientId: number | null;
	sourceOrcId: number;
	sourceOitId: number;
	sourceRole: string | null;
}

/** Vínculo cross-tenant lido do banco do destino. */
export interface CrossTenantLinkRead {
	id: number;
	sourceTenantId: string;
	sourceClientId: number | null;
	sourceOrcId: number | null;
	sourceOitId: number | null;
	targetTenantId: string;
	targetClientId: number;
	targetOrcId: number;
	targetOitId: number;
	createdByUserLogin: string | null;
	sources: CrossTenantLinkSourceRead[];
}

/**
 * Lê o vínculo cross-tenant de um item de destino (orc/oit) — para reexibir/
 * reidratar um pedido já vinculado. Usa o override de operação quando há
 * contexto roteado ativo; caso contrário, o token normal do tenant destino
 * (ex.: visualização em Detalhes no próprio tenant). Retorna null se não houver.
 */
export async function fetchCrossTenantLink(
	targetOrcId: number,
	targetOitId: number,
): Promise<CrossTenantLinkRead | null> {
	const context = QuotationOperationContextStore.get();
	const override: ApiOverrideOptions | undefined = context
		? {
				accessTokenOverride: context.operationAccessToken,
				tenantIdOverride: context.effectiveTenantId,
			}
		: undefined;

	const params = new URLSearchParams({
		targetOrcId: String(targetOrcId),
		targetOitId: String(targetOitId),
	});

	const response = (await api(
		`/cross-tenant/order-link?${params.toString()}`,
		{ method: "GET" },
		override,
	)) as { success?: boolean; data?: { linked?: boolean; link?: CrossTenantLinkRead | null } };

	return response?.data?.link ?? null;
}

/**
 * Lê TODOS os vínculos cross-tenant de um pedido de destino (por orc_id) — para
 * a tela de Detalhes reexibir os vínculos de um pedido já criado. Usa o override
 * de operação quando há contexto roteado ativo; caso contrário, o token normal
 * do tenant (Detalhes no próprio tenant destino). Retorna [] quando não houver.
 */
export async function fetchCrossTenantLinksByOrder(
	targetOrcId: number,
): Promise<CrossTenantLinkRead[]> {
	const context = QuotationOperationContextStore.get();
	const override: ApiOverrideOptions | undefined = context
		? {
				accessTokenOverride: context.operationAccessToken,
				tenantIdOverride: context.effectiveTenantId,
			}
		: undefined;

	const params = new URLSearchParams({ targetOrcId: String(targetOrcId) });

	const response = (await api(
		`/cross-tenant/order-links?${params.toString()}`,
		{ method: "GET" },
		override,
	)) as { success?: boolean; data?: { links?: CrossTenantLinkRead[] } };

	return response?.data?.links ?? [];
}

/**
 * Verifica se um item de ORIGEM (orc+oit) já possui vínculo cross-tenant — para
 * avisar o operador ao montar o vínculo (SPEC-17). Usa o override de operação
 * (o vínculo vive no destino). Best-effort no chamador: uma falha aqui não deve
 * impedir a adição do contribuinte.
 */
export async function checkSourceItemLinked(
	sourceOrcId: number,
	sourceOitId: number,
): Promise<{ linked: boolean; links: CrossTenantLinkRead[] }> {
	const context = QuotationOperationContextStore.get();
	const override: ApiOverrideOptions | undefined = context
		? {
				accessTokenOverride: context.operationAccessToken,
				tenantIdOverride: context.effectiveTenantId,
			}
		: undefined;

	const params = new URLSearchParams({
		sourceOrcId: String(sourceOrcId),
		sourceOitId: String(sourceOitId),
	});
	if (context?.sourceTenantId) params.set("sourceTenantId", context.sourceTenantId);

	const response = (await api(
		`/cross-tenant/order-link/by-source?${params.toString()}`,
		{ method: "GET" },
		override,
	)) as {
		success?: boolean;
		data?: { linked?: boolean; links?: CrossTenantLinkRead[] };
	};

	return {
		linked: Boolean(response?.data?.linked),
		links: response?.data?.links ?? [],
	};
}

/** Item de origem a ser marcado como terceirizado (SPEC-17). */
export interface OutsourceSourceItem {
	orcId: number;
	oitId: number;
}

export interface MarkOutsourcedResult {
	requested: number;
	moved: number;
	alreadyOutsourced: number;
	missing: OutsourceSourceItem[];
}

/**
 * Move o(s) item(ns) de ORIGEM para "Aguardando Produção Terceirizada" (status
 * 14) após o vínculo. Usa o token NORMAL (origem) — NUNCA o override de operação,
 * pois a mudança é no banco da origem com a permissão do próprio usuário.
 * Lança em erro/bloqueio; o chamador trata (best-effort, não desfaz o vínculo).
 */
export async function markSourceItemsOutsourced(
	items: OutsourceSourceItem[],
): Promise<MarkOutsourcedResult> {
	// A mudança de status é no banco da ORIGEM. Com contexto de operação ativo, o
	// api() herdaria o override do DESTINO — por isso forçamos o tenant da origem
	// com o token NORMAL (mesmo padrão do lookup do pedido de origem).
	const context = QuotationOperationContextStore.get();
	if (!context?.sourceTenantId) {
		throw new Error(
			"Contexto de origem indisponível para atualizar o status dos itens.",
		);
	}
	const override: ApiOverrideOptions = {
		accessTokenOverride: TokenService.getAccessToken() ?? undefined,
		tenantIdOverride: context.sourceTenantId,
	};

	const response = (await api(
		"/cross-tenant/source-items/outsourced",
		{ method: "POST", body: JSON.stringify({ items }) },
		override,
	)) as { success?: boolean; data?: MarkOutsourcedResult };

	if (!response?.success || !response.data) {
		throw new Error("Falha ao atualizar o status dos itens de origem.");
	}
	return response.data;
}
